import { useEffect, useState } from "react";
import { Alert, Button, List, message, Modal, Skeleton, Space, Tag, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import { WorkerDeletionInfo } from "../../types/workerDeletion.type";
import { UserRole } from "../../types/user.type";
import { useAuth } from "../../contexts/auth.context";

const { Text, Paragraph } = Typography;

interface DeleteWorkerModalProps {
  workerId: string;
  workerName: string;
  workerSurname?: string;
  open: boolean;
  /** Модалка открыта из-за попытки оплатить зарплату без долга */
  fromPayment?: boolean;
  onClose: () => void;
  onDeleted: () => void;
  /** Провести оплату несмотря на отсутствие долга */
  onPayAnyway?: () => void;
}

export default function DeleteWorkerModal({
  workerId,
  workerName,
  workerSurname,
  open,
  fromPayment = false,
  onClose,
  onDeleted,
  onPayAnyway,
}: DeleteWorkerModalProps) {
  const { authState } = useAuth();
  const [info, setInfo] = useState<WorkerDeletionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canDelete = authState.user?.role === UserRole.Admin;
  const fullName = `${workerName} ${workerSurname ?? ""}`.trim();

  useEffect(() => {
    if (!open || !workerId) return;

    const fetchInfo = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/worker/${workerId}/deletion-info`);
        setInfo(response.data);
      } catch (error) {
        console.error("Error fetching worker deletion info", error);
        message.error("Ошибка получения данных сотрудника.");
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [open, workerId]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/worker/${workerId}`);
      message.success("Сотрудник успешно удален.");
      onDeleted();
      onClose();
    } catch (error: any) {
      console.error("Error deleting worker", error);
      message.error(error.response?.data?.error ?? "Ошибка при удалении сотрудника.");
    } finally {
      setDeleting(false);
    }
  };

  const handlePayAnyway = () => {
    onPayAnyway?.();
    onClose();
  };

  const handleAfterClose = () => {
    setInfo(null);
  };

  const affected = info
    ? [
        info.reportsCount > 0
          ? `Отчёты по зарплате: ${info.reportsCount} (переносятся в архив)`
          : null,
        info.positions.length > 0
          ? `Должности: ${info.positions.map((position) => position.name).join(", ")}`
          : null,
        info.pricingLogsCount > 0 ? `История изменения цен: ${info.pricingLogsCount}` : null,
        info.pendingRequestsCount > 0
          ? `Необработанные заявки на зарплату: ${info.pendingRequestsCount}`
          : null,
        info.workLogsCount > 0 ? `Записи о работе: ${info.workLogsCount}` : null,
      ].filter((item): item is string => item !== null)
    : [];

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          {fromPayment ? "Долг по зарплате отсутствует" : "Удаление сотрудника"}
        </Space>
      }
      open={open}
      onCancel={onClose}
      afterClose={handleAfterClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Отмена
        </Button>,
        onPayAnyway ? (
          <Button key="pay" onClick={handlePayAnyway} disabled={loading}>
            Всё равно оплатить
          </Button>
        ) : null,
        <Button
          key="delete"
          danger
          type="primary"
          loading={deleting}
          disabled={!canDelete || loading}
          onClick={handleDelete}
        >
          Удалить сотрудника
        </Button>,
      ]}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          {fromPayment && info && (
            <Paragraph>
              У сотрудника <Text strong>{fullName}</Text> долг{" "}
              <Text strong>
                {Math.round(info.debt).toLocaleString()} {info.salaryCurrency}
              </Text>
              . Оплата не требуется — можно удалить сотрудника или всё равно провести оплату.
            </Paragraph>
          )}

          <Paragraph>
            Удалить сотрудника <Text strong>{fullName}</Text> из системы?
          </Paragraph>

          {affected.length > 0 && (
            <List
              size="small"
              header={<Text strong>Будет удалено</Text>}
              bordered
              dataSource={affected}
              renderItem={(item) => <List.Item>{item}</List.Item>}
              style={{ marginBottom: 16 }}
            />
          )}

          {info && info.positions.length > 0 && (
            <Space wrap style={{ marginBottom: 16 }}>
              {info.positions.map((position) => (
                <Tag key={position._id} color="blue">
                  {position.name}
                </Tag>
              ))}
            </Space>
          )}

          <Alert
            type="warning"
            showIcon
            message="Действие необратимо"
            description="Сотрудник будет удалён из зарплат и всех должностей. Транзакции по кассе сохранятся."
          />

          {!canDelete && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Удалять сотрудников может только администратор."
            />
          )}
        </>
      )}
    </Modal>
  );
}
